import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import WeddingPartyModal from "../WeddingPartyModal";
import { GetWeddingPartyQuery } from "@src/@types/api.d";
import { motion } from "framer-motion";
import { WeddingPartyType } from "@src/components/CustomTemplate2/WeddingParty";

export type IWeddingParty = {
  eventId: number;
  weddingPartyData: GetWeddingPartyQuery;
  loading?: boolean;
  sectionImage?: string;
};

const AUTO_PLAY_INTERVAL = 4000; // 4 seconds

const WeddingParty: React.FC<IWeddingParty> = ({
  eventId,
  weddingPartyData,
}) => {
  const weddingPartyMembers = (weddingPartyData?.getWeddingParty || []).filter(
    (member): member is WeddingPartyType =>
      member !== null &&
      !!member.first_name &&
      !!member.last_name &&
      !!member.role
  );

  const sortedMembers = [...weddingPartyMembers].sort((a, b) => {
    if (a?.order_index !== null && b?.order_index !== null) {
      return a?.order_index! - b?.order_index!;
    }
    if (a?.order_index !== null && b?.order_index === null) return -1;
    if (a?.order_index === null && b?.order_index !== null) return 1;
    return 0;
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isAutoPlaying && sortedMembers.length > 1 && !isModalOpen) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) =>
          prev === sortedMembers.length - 1 ? 0 : prev + 1
        );
      }, AUTO_PLAY_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, sortedMembers.length, isModalOpen]);

  const handleMouseEnter = () => {
    if (!isModalOpen) setIsAutoPlaying(false);
  };
  const handleMouseLeave = () => {
    if (!isModalOpen) setIsAutoPlaying(true);
  };

  const goToSlide = (idx: number) => {
    if (isModalOpen) return;
    setCurrentIndex(idx);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 6000);
  };

  const nextSlide = () => {
    if (isModalOpen) return;
    const nextIdx = (currentIndex + 1) % sortedMembers.length;
    goToSlide(nextIdx);
  };

  const prevSlide = () => {
    if (isModalOpen) return;
    const prevIdx =
      currentIndex === 0 ? sortedMembers.length - 1 : currentIndex - 1;
    goToSlide(prevIdx);
  };

  const getWrappedIndex = (index: number) => {
    if (index < 0) return sortedMembers.length + index;
    return index % sortedMembers.length;
  };

  if (!sortedMembers.length) return null;

  const prevIndex = getWrappedIndex(currentIndex - 1);
  const nextIndex = getWrappedIndex(currentIndex + 1);

  const getCardPosition = (position: "prev" | "current" | "next") => {
    const positions = {
      prev: { x: 40, scale: 0.85, opacity: 0.6, zIndex: 1 },
      current: { x: 0, scale: 1, opacity: 1, zIndex: 10 },
      next: { x: -40, scale: 0.85, opacity: 0.6, zIndex: 1 },
    };
    return positions[position];
  };

  const cardTransition = {
    type: "spring",
    stiffness: 300,
    damping: 35,
    duration: 0.7,
  };

  const renderCard = (
    member: WeddingPartyType,
    position: "prev" | "current" | "next",
    onClick?: () => void
  ) => {
    const positionConfig = getCardPosition(position);
    const isCurrentCard = position === "current";

    const cardContent = (
      <motion.div
        animate={positionConfig}
        transition={cardTransition}
        className={`relative mx-auto transform transition-all duration-300 pointer-events-auto
          ${position === "current" ? "z-10" : "z-5"}
          ${
            position !== "current"
              ? "pointer-events-none hover:scale-90 hover:opacity-80"
              : ""
          }`}
        style={{ cursor: "pointer" }}
        onClick={onClick}
      >
        <div
          className={`flex flex-col bg-[#EBE3CE] rounded-2xl overflow-hidden transition-all duration-300 relative
          w-60 h-80 md:w-80 md:h-[420px] lg:w-[360px] lg:h-[420px]
          ${
            position === "current"
              ? "shadow-2xl hover:-translate-y-2 hover:shadow-3xl"
              : "brightness-90 shadow-lg hover:brightness-100 hover:-translate-y-1"
          }`}
        >
          <div className="relative w-full h-52 md:h-64 lg:h-72 overflow-hidden bg-transparent">
            {member?.image_url ? (
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(300, 400)
                )}`}
                layout="fill"
                objectFit="cover"
                objectPosition="top"
                src={member.image_url}
                alt={`${member.first_name} ${member.last_name}`}
                className="transition-transform duration-300 hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#8C7756] to-amber-600">
                <span className="text-2xl md:text-3xl lg:text-5xl font-semibold text-white uppercase">
                  {member?.first_name.charAt(0)}
                  {member?.last_name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          <div className="p-4 md:p-5 lg:p-6 h-28 md:h-[8.5rem] flex flex-col justify-start">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-[#292524] mb-1 leading-tight font-neue">
              {member?.first_name}{" "}
              {member?.nickname ? `"${member.nickname}"` : ""}{" "}
              {member?.last_name}
            </h3>
            <p
              className={`${
                member?.role.length ?? 0 > 12 ? "text-xs" : "text-sm"
              } md:text-base text-[#8C7756] mb-2 uppercase tracking-widest font-medium font-calabassas`}
            >
              {member?.role}
            </p>
            {member?.story && (
              <article
                className="text-sm md:text-base font-light leading-loose text-[#57534e] flex-grow overflow-hidden font-inter"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  lineClamp: 1,
                  textOverflow: "ellipsis",
                }}
              >
                {member.story}
              </article>
            )}
          </div>
        </div>
      </motion.div>
    );

    if (isCurrentCard) {
      return (
        <Dialog trigger={cardContent} onOpenChange={setIsModalOpen}>
          <WeddingPartyModal
            name={
              member?.nickname
                ? `${member?.first_name} "${member?.nickname}" ${member?.last_name}`
                : `${member?.first_name} ${member?.last_name}`
            }
            nickname={undefined}
            role={member?.role ?? ""}
            story={member?.story || ""}
            imageUrl={member?.image_url || ""}
          />
        </Dialog>
      );
    }

    return cardContent;
  };

  return (
    <div
      className="w-full py-16 md:py-12 lg:py-20 overflow-hidden relative border-b"
      style={{
        background:
          "linear-gradient(135deg, rgba(255, 182, 193, 0.1) 0%, rgba(255, 240, 245, 0.1) 50%, rgba(230, 230, 250, 0.1) 100%), #8C7756",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="text-center mb-12 relative z-10">
        <h2 className="text-[#EBE3CE] text-3xl md:text-4xl lg:text-5xl font-normal leading-tight tracking-wide font-calabassas">
          Meet Our Wedding Party
        </h2>
      </div>

      <div className="flex items-center justify-center relative w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-8">
        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          aria-label="Previous"
          className="absolute left-2 md:left-4 lg:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 lg:w-12 lg:h-12 rounded-full bg-[#EBE3CE]/90 border-none text-[#8C7756] cursor-pointer transition-all duration-300 flex items-center justify-center shadow-lg hover:bg-[#EBE3CE] hover:scale-110"
        >
          <svg
            className="w-5 h-5 md:w-6 md:h-6 lg:w-6 lg:h-6"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Carousel Track */}
        <div className="flex items-center justify-center relative w-full h-80 md:h-[420px] lg:h-[480px]">
          {sortedMembers.length > 1 &&
            renderCard(sortedMembers[prevIndex], "prev", prevSlide)}
          {renderCard(sortedMembers[currentIndex], "current")}
          {sortedMembers.length > 1 &&
            renderCard(sortedMembers[nextIndex], "next", nextSlide)}
        </div>

        <button
          onClick={nextSlide}
          aria-label="Next"
          className="absolute right-2 md:right-4 lg:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 lg:w-12 lg:h-12 rounded-full bg-[#EBE3CE]/90 border-none text-[#8C7756] cursor-pointer transition-all duration-300 flex items-center justify-center shadow-lg hover:bg-[#EBE3CE] hover:scale-110"
        >
          <svg
            className="w-5 h-5 md:w-6 md:h-6 lg:w-6 lg:h-6"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Dots Indicator */}
      {sortedMembers.length > 1 && (
        <div className="flex justify-center items-center gap-3 mt-8 relative z-15">
          {sortedMembers.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`w-3 h-3 rounded-full border-none cursor-pointer transition-all duration-300 
                ${
                  idx === currentIndex
                    ? "bg-[#EBE3CE] scale-125 shadow-lg ring-2 ring-[#EBE3CE]/30"
                    : "bg-[#EBE3CE]/40 hover:bg-[#EBE3CE]/70 hover:scale-110"
                }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default WeddingParty;
